import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Business as BusinessIcon,
  ReceiptLong as ReceiptLongIcon,
  PersonOutline as PersonOutlineIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  EditNote as EditNoteIcon,
  ImageOutlined as ImageOutlinedIcon,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import useAlert from "../../../../hooks/useAlert";
import { validatePrintTemplateSetting } from "../../../../utils/validation";
import {
  AddPrintTemplateSetting,
  GetPrintTemplateSettingAction,
  PrintTemplateSettingUpdate,
} from "../../../../redux/actions/printTemplateSettingActions";
import SectionCard from "../SectionCard";
import SettingTile from "../SettingTile";

const DEFAULT_VALUES = {
  companyDisplayName: "",
  logoUrl: "",
  taxNumber: "",
  taxOffice: "",
  customHeaderText: "",
  footerText: "",
  showLogo: false,
  showTaxInfo: false,
  showOrderDate: true,
  showOrderNumber: true,
  showMarketplaceName: true,
  showStoreName: true,
  showCargoBarcode: true,
  showCargoCompany: true,
  showCampaignCode: true,
  showCustomerAddress: true,
  showCustomerEmail: false,
  showCustomerPhone: false,
  showProductImage: true,
  showProductDescription: true,
  showUnitPrice: false,
  showTotalPrice: false,
  showVat: false,
};

const SWITCH_GROUPS = [
  {
    key: "order",
    title: "Sipariş Bilgileri",
    icon: ReceiptLongIcon,
    fields: [
      { name: "showOrderDate", label: "Sipariş Tarihi" },
      { name: "showOrderNumber", label: "Sipariş Numarası" },
      { name: "showMarketplaceName", label: "Pazaryeri Adı" },
      { name: "showStoreName", label: "Mağaza Adı" },
      { name: "showCargoBarcode", label: "Kargo Barkodu" },
      { name: "showCargoCompany", label: "Kargo Firması" },
      { name: "showCampaignCode", label: "Kampanya Kodu" },
    ],
  },
  {
    key: "customer",
    title: "Müşteri Bilgileri",
    icon: PersonOutlineIcon,
    fields: [
      { name: "showCustomerAddress", label: "Teslimat Adresi" },
      { name: "showCustomerEmail", label: "E-posta" },
      { name: "showCustomerPhone", label: "Telefon" },
    ],
  },
  {
    key: "product",
    title: "Ürün Bilgileri",
    icon: Inventory2OutlinedIcon,
    fields: [
      { name: "showProductImage", label: "Ürün Görseli" },
      { name: "showProductDescription", label: "Ürün Açıklaması" },
      { name: "showUnitPrice", label: "Birim Fiyat" },
      { name: "showTotalPrice", label: "Toplam Fiyat" },
      { name: "showVat", label: "KDV" },
    ],
  },
];

const OrderPrintSettings = () => {
  const authUserId = useSelector((state) => state?.auth?.user?.id);
  const dispatch = useDispatch();
  const { showSuccessPrintTemplateSetting, showErrorAlertPrintTemplateSetting } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState(DEFAULT_VALUES);
  const [dataExists, setDataExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: checked }));
  };

  const buildPayload = () => ({
    ...formValues,
    userId: authUserId,
    name: "Varsayılan Şablon",
    isDefault: true,
    isActive: true,
    productDescriptionMaxLen: 1850,
    paperSize: "A4",
    orientation: "Portrait",
    fontSizePt: 12,
    logoPosition: "Left",
  });

  const printTemplateAdd = async (e) => {
    e.preventDefault();
    const errors = validatePrintTemplateSetting(formValues);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      try {
        setSubmitting(true);
        const response = await dispatch(AddPrintTemplateSetting(buildPayload()));
        if (response?.data) {
          setFormValues((prev) => ({ ...prev, ...response.data }));
        }
        setDataExists(true);
        showSuccessPrintTemplateSetting();
      } catch {
        showErrorAlertPrintTemplateSetting();
      } finally {
        setSubmitting(false);
      }
    }
  };

  const printTemplateUpdate = async (e) => {
    e.preventDefault();
    const errors = validatePrintTemplateSetting(formValues);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      try {
        setSubmitting(true);
        await dispatch(PrintTemplateSettingUpdate(buildPayload()));
        showSuccessPrintTemplateSetting();
      } catch {
        showErrorAlertPrintTemplateSetting();
      } finally {
        setSubmitting(false);
      }
    }
  };

  const getPrintTemplateSetting = async () => {
    try {
      setLoading(true);
      const data = await dispatch(GetPrintTemplateSettingAction(authUserId));
      if (data && data.length > 0) {
        setFormValues((prev) => ({ ...prev, ...data[0] }));
        setDataExists(true);
      }
    } catch (error) {
      console.error("Error fetching print template setting:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPrintTemplateSetting();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: { xs: "200px", sm: "300px" },
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
    },
  };

  return (
    <Box
      component="form"
      onSubmit={dataExists ? printTemplateUpdate : printTemplateAdd}
      sx={{
        mt: { xs: 1, sm: 1.5 },
        width: "100%",
      }}
    >
      <Alert severity="info" variant="outlined" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        Bu ayarlar, Siparişler sayfasındaki "Yazdır" butonuyla oluşturulan çıktının görünümünü
        belirler.
      </Alert>

      <SectionCard
        icon={BusinessIcon}
        title="Marka & Vergi Bilgileri"
        description="Yazdırma çıktısının üst kısmında görünecek firma kimliği"
      >
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12}>
            <TextField
              size={isMobile ? "small" : "medium"}
              name="companyDisplayName"
              fullWidth
              label="Yazdırmada Görünecek Firma Adı"
              value={formValues.companyDisplayName || ""}
              onChange={handleInputChange}
              sx={textFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              size={isMobile ? "small" : "medium"}
              name="logoUrl"
              fullWidth
              label="Logo URL"
              value={formValues.logoUrl || ""}
              onChange={handleInputChange}
              sx={textFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ImageOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SettingTile
              label="Logoyu Göster"
              checked={!!formValues.showLogo}
              name="showLogo"
              onChange={handleSwitchChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size={isMobile ? "small" : "medium"}
              name="taxNumber"
              fullWidth
              label="Vergi Numarası"
              value={formValues.taxNumber || ""}
              onChange={handleInputChange}
              error={!!formErrors.taxNumber}
              helperText={formErrors.taxNumber}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size={isMobile ? "small" : "medium"}
              name="taxOffice"
              fullWidth
              label="Vergi Dairesi"
              value={formValues.taxOffice || ""}
              onChange={handleInputChange}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SettingTile
              label="Vergi Bilgilerini Göster"
              checked={!!formValues.showTaxInfo}
              name="showTaxInfo"
              onChange={handleSwitchChange}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {SWITCH_GROUPS.map((group) => (
        <SectionCard key={group.key} icon={group.icon} title={group.title}>
          <Grid container spacing={{ xs: 1.25, sm: 1.5 }}>
            {group.fields.map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field.name}>
                <SettingTile
                  label={field.label}
                  checked={!!formValues[field.name]}
                  name={field.name}
                  onChange={handleSwitchChange}
                />
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      ))}

      <SectionCard
        icon={EditNoteIcon}
        title="Metin Ayarları"
        description="Çıktıdaki üst ve alt bilgi metinlerini özelleştirin"
      >
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12}>
            <TextField
              size={isMobile ? "small" : "medium"}
              name="customHeaderText"
              fullWidth
              label="Üst Bilgi Metni"
              placeholder="Siparişleri Yazdır"
              value={formValues.customHeaderText || ""}
              onChange={handleInputChange}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size={isMobile ? "small" : "medium"}
              name="footerText"
              fullWidth
              label="Alt Bilgi (Footer) Metni"
              placeholder="localhost:5173/orders"
              value={formValues.footerText || ""}
              onChange={handleInputChange}
              sx={textFieldSx}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: { xs: 1, sm: 2 },
          mb: { xs: 1, sm: 2 },
        }}
      >
        <Button
          type="submit"
          variant="contained"
          sx={{
            borderRadius: 1.5,
            backgroundColor: "#1F1B45",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.813rem", sm: "0.875rem" },
            padding: { xs: "8px 20px", sm: "10px 28px" },
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: { xs: "120px", sm: "150px" },
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#2C285A",
              boxShadow: "none",
            },
            "& .MuiCircularProgress-root": {
              width: { xs: "20px !important", sm: "24px !important" },
              height: { xs: "20px !important", sm: "24px !important" },
            },
          }}
          disabled={submitting}
        >
          {submitting ? (
            <CircularProgress size={isMobile ? 20 : 24} sx={{ color: "inherit" }} />
          ) : (
            "Kaydet"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default OrderPrintSettings;
